// What is out right now — and what it is doing.
//
// This appears at rung 2 and not before, because with one phone and one
// job at a time the board is a list of one and the composer already
// showed it to you.
//
// The shape is the profile's. A restaurant is a **queue** — orders arrive
// between 18:00 and 21:00 and get handed to whichever rider comes free,
// so what matters is the waiting line and who is loose. A courier fleet
// is a **board** — planned in the morning, reflowed all day, and a
// problem has to be visible at 14:00 rather than at 18:00. Same records
// underneath; different question being asked of them.
//
// **Every live claim on this board comes from the run's own records**,
// through `@wayfind/core`'s honesty model, exactly as the customer's page
// derives them. This console is one more subscriber holding a link it
// minted — not a privileged observer — so there is no second reading of
// the truth here to keep honest. What it may say that a customer's page
// may not is per-stop: the dispatcher holds the plan (§20.8 keeps it off
// the wire), so a progress count and a next-stop arrival are theirs to
// compute.

import { useMemo, useState } from "react";
import {
  STOP_OUTCOME,
  stopProgress,
  type FollowSnapshot
} from "@wayfind/core";
import { Note, Panel } from "./Bits";
import { clockWords, distanceWords, plural, type Vocabulary } from "../ops/words";
import type { FleetState, StoredJob } from "../ops/state";
import { nowSeconds } from "../config";

const DAY = 86400;

export function DayBoard({
  state,
  vocabulary,
  live,
  onHandOver,
  onFinish,
  onOpen
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  /** The newest snapshot per job id, for everything still open. */
  live: Map<string, FollowSnapshot>;
  onHandOver(job: StoredJob): void;
  onFinish(job: StoredJob): void;
  onOpen(job: StoredJob): void;
}): JSX.Element | null {
  const now = nowSeconds();
  const today = useMemo(
    () => state.jobs
      .filter(job => now - job.createdUnixSeconds < DAY)
      .slice()
      .sort((a, b) => b.createdUnixSeconds - a.createdUnixSeconds),
    [state.jobs, now]
  );
  const [showDone, setShowDone] = useState(false);

  if (!today.length) return null;

  const waiting = today.filter(job => !job.heldBy && job.finishedUnixSeconds == null);
  const out = today.filter(job => job.heldBy && job.finishedUnixSeconds == null);
  const done = today.filter(job => job.finishedUnixSeconds != null);
  const queue = state.profile === "restaurant";

  // A round in trouble is the whole reason this board exists at 14:00
  // rather than at 18:00, so it is counted where the eye lands first.
  const trouble = out.filter(job => {
    const snapshot = live.get(job.id);
    if (!snapshot) return false;
    return snapshot.status.outcomes.some(
      outcome => outcome === STOP_OUTCOME.SKIPPED || outcome === STOP_OUTCOME.FAILED
    );
  }).length;

  return (
    <Panel
      eyebrow={queue ? "Tonight" : "Today"}
      aside={
        done.length ? (
          <button type="button" className="button button--small" onClick={() => setShowDone(v => !v)}>
            {showDone ? "Hide finished" : `${done.length} finished`}
          </button>
        ) : null
      }
    >
      {trouble ? (
        <Note
          title={`${trouble} ${plural(trouble, vocabulary.stop)} not delivered`}
          tone="halt"
        >
          A {vocabulary.driver} has marked a {vocabulary.stop} skipped or failed on a round still
          out. The {vocabulary.recipient} was told at the same moment you were — this is the
          same record, not a copy of it.
        </Note>
      ) : null}

      {waiting.length ? (
        <>
          <p className="field__label" style={{ marginBottom: "0.35rem" }}>
            {queue ? "Waiting for a rider" : "Nobody holding it"}
          </p>
          <div className="board" style={{ marginBottom: "0.8rem" }}>
            {waiting.map(job => (
              <Row
                key={job.id}
                job={job}
                state={state}
                vocabulary={vocabulary}
                snapshot={live.get(job.id) ?? null}
                tone="queued"
                onHandOver={onHandOver}
                onFinish={onFinish}
                onOpen={onOpen}
              />
            ))}
          </div>
        </>
      ) : null}

      <p className="field__label" style={{ marginBottom: "0.35rem" }}>
        {queue ? "Out with a rider" : "Out"}
      </p>
      <div className="board">
        {out.length
          ? out.map(job => (
            <Row
              key={job.id}
              job={job}
              state={state}
              vocabulary={vocabulary}
              snapshot={live.get(job.id) ?? null}
              tone="out"
              onHandOver={onHandOver}
              onFinish={onFinish}
              onOpen={onOpen}
            />
          ))
          : <p className="small muted">Nothing out.</p>}
      </div>

      {showDone && done.length ? (
        <>
          <hr className="divider" />
          <div className="board">
            {done.map(job => (
              <Row
                key={job.id}
                job={job}
                state={state}
                vocabulary={vocabulary}
                snapshot={live.get(job.id) ?? null}
                tone="done"
                onHandOver={onHandOver}
                onFinish={onFinish}
                onOpen={onOpen}
              />
            ))}
          </div>
        </>
      ) : null}

      <hr className="divider" />
      <Note title="This board reads the runs' own records." tone="quiet">
        Every line below the {vocabulary.driver}’s name comes off the mesh, from the same signed
        records the {vocabulary.recipient} watching that link is reading — this console holds the
        link because it minted it, and subscribes like anybody else. Nothing was reported to a
        server, because there is no server. A round nobody is publishing to says so rather than
        going quiet.
      </Note>
    </Panel>
  );
}

/**
 * One job: who has it, and what the run says about it.
 *
 * The two halves are deliberately distinct. The top line is what this
 * console *did* — sealed to a phone, at a time — and it is true whether
 * or not anybody is listening. Everything under it is what the run has
 * *said*, and it is absent rather than optimistic when the run has said
 * nothing.
 */
function Row({
  job,
  state,
  vocabulary,
  snapshot,
  tone,
  onHandOver,
  onFinish,
  onOpen
}: {
  job: StoredJob;
  state: FleetState;
  vocabulary: Vocabulary;
  snapshot: FollowSnapshot | null;
  tone: "queued" | "out" | "done";
  onHandOver(job: StoredJob): void;
  onFinish(job: StoredJob): void;
  onOpen(job: StoredJob): void;
}): JSX.Element {
  const device = state.devices.find(entry => entry.id === job.deviceId) ?? null;
  const who = !job.heldBy
    ? "Nobody yet"
    : device?.name ?? job.driverName ?? "Me";
  const what = job.stops.length === 1
    ? job.stops[0]!.label
    : `${job.stops[0]!.label} → ${plural(job.stops.length, vocabulary.stop)}`;

  return (
    <div className={`boardrow boardrow--${tone === "done" ? "settled" : tone}`}>
      <div>
        <p className="boardrow__who">{who}</p>
        <p className="rowcard__sub">
          {clockWords(job.createdUnixSeconds)}
          {job.totalMeters ? ` · ${distanceWords(job.totalMeters)}` : ""}
          {job.serviceDay ? ` · day ${job.serviceDay}` : ""}
        </p>
      </div>
      <div className="row row--tight">
        <button type="button" className="button button--small" onClick={() => onOpen(job)}>
          Links
        </button>
        {tone !== "done" ? (
          <>
            <button type="button" className="button button--small" onClick={() => onHandOver(job)}>
              Hand over
            </button>
            <button type="button" className="button button--small button--ghost" onClick={() => onFinish(job)}>
              Done
            </button>
          </>
        ) : null}
      </div>
      <p className="boardrow__what">{what}</p>
      <LiveLine job={job} snapshot={snapshot} vocabulary={vocabulary} settled={tone === "done"} />
    </div>
  );
}

/**
 * What the run has said about itself, in one line.
 *
 * Absent when nothing has been heard and the job is settled — a round
 * that finished this morning does not need a row explaining that nobody
 * is broadcasting any more. Present and explicit while it is out,
 * because "we have heard nothing from this driver" is exactly the fact a
 * dispatcher needs at 14:00 and is the one a hopeful UI hides.
 */
function LiveLine({
  job,
  snapshot,
  vocabulary,
  settled
}: {
  job: StoredJob;
  snapshot: FollowSnapshot | null;
  vocabulary: Vocabulary;
  settled: boolean;
}): JSX.Element | null {
  if (!snapshot) {
    if (settled) return null;
    return (
      <p className="boardrow__live small muted">
        Not heard from yet.
      </p>
    );
  }

  const { status, arrival } = snapshot;
  const plan = snapshot.plan;
  const progress = stopProgress(status, plan);
  const heard = status.observed.ever;

  if (!heard) {
    return (
      <p className="boardrow__live small muted">
        {settled ? "Nothing was ever published." : `Sealed, and the ${vocabulary.driver} has not started publishing.`}
      </p>
    );
  }

  const bits: string[] = [];

  // Progress first: it is what a dispatcher scans a board for.
  if (progress.total > 0) {
    bits.push(`${progress.delivered}/${progress.total} delivered`);
  }
  if (progress.skipped) bits.push(`${progress.skipped} skipped`);
  if (progress.failed) bits.push(`${progress.failed} failed`);

  // Then the position claim, in the honesty model's own words — never
  // "live" for a stale fix and never a position at all for a coarse run.
  if (status.position.kind === "live") {
    bits.push("live position");
  } else if (status.position.kind === "stale") {
    const minutes = Math.max(1, Math.round(status.position.ageSeconds / 60));
    bits.push(`no fix for ${minutes} min`);
  } else if (status.position.kind === "withheld") {
    bits.push("stops only");
  } else if (status.position.kind === "unplaceable") {
    bits.push("position off this console’s map");
  }

  // And the arrival, only where one is actually claimed. This console
  // normally gets a real `estimate` rather than the customer's floor,
  // because it holds the plan the wire does not carry (§20.8) — but a
  // route day published under a term link reaches us plan-less too, and
  // "not before" is the only honest word for that.
  if (arrival.kind === "estimate") {
    const at = new Date(arrival.arrivalMillis);
    bits.push(`about ${at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`);
  } else if (arrival.kind === "no-earlier-than") {
    const at = new Date(arrival.arrivalMillis);
    bits.push(`not before ${at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`);
  } else if (arrival.kind === "no-map") {
    bits.push("no map for an arrival time");
  }

  const stale = status.observed.stale;
  const tone = status.outcomes.some(
    outcome => outcome === STOP_OUTCOME.SKIPPED || outcome === STOP_OUTCOME.FAILED
  )
    ? "boardrow__live--halt"
    : stale
      ? "boardrow__live--quiet"
      : "boardrow__live--live";

  return (
    <p className={`boardrow__live small ${tone}`}>
      {bits.join(" · ")}
      {status.lastOutcome?.hasPhoto ? " · photo at the door" : ""}
      {stale && status.observed.ageSeconds != null
        ? ` · last heard ${Math.max(1, Math.round(status.observed.ageSeconds / 60))} min ago`
        : ""}
      {job.finishedUnixSeconds != null && !settled ? " · finishing" : ""}
    </p>
  );
}
